import { useLocalStorage } from '@vueuse/core'
import { LocalStorage } from '~/constants/local-storage'
import { OrganizationRoles } from '~/constants/organizations'
import { AppRoutes, OrganizationRoutes } from '~/constants/routes'
import { useNavigationStore } from '~/store/navigation'
import { useOrganizationsStore } from '~/store/organizations'
import { useWorkspacesStore } from '~/store/workspaces'

export default defineNuxtRouteMiddleware(async (to) => {
  const navigationStore = useNavigationStore()
  const organizationsStore = useOrganizationsStore()
  const workspacesStore = useWorkspacesStore()
  const { lastOrganizationURLPath, lastWorkspaceURLPath } = storeToRefs(navigationStore)
  const { organizations, selectedOrganization } = storeToRefs(organizationsStore)
  const { workspaces } = storeToRefs(workspacesStore)

  const lastOrganizationId = useLocalStorage(LocalStorage.Organizations.LastVisited, '')

  // ✅ AJOUTER la gestion d'erreur ici
  try {
    // TODO: Update check
    if (!organizations.value.length) await organizationsStore.getUserOrganizations()
  }
  catch (error) {
    console.error('Error fetching user organizations:', error)
    // Rediriger vers la page de login ou d'erreur selon le contexte
    const { $sentryCaptureException } = useNuxtApp()
    $sentryCaptureException(error)

    // Si c'est un problème d'authentification, rediriger vers login
    if (error.code === 'permission-denied' || error.code === 'unauthenticated') {
      return navigateTo('/auth/login')
    }

    // Pour d'autres erreurs, rediriger vers la page de sélection d'organisation
    return navigateTo(AppRoutes.SelectOrganization)
  }

  if (lastOrganizationURLPath.value !== to.params.organizationURLPath) {
    const organizationToSelect = organizations.value.find(
      organization => organization.urlPath === to.params.organizationURLPath,
    )

    if (organizationToSelect) {
      organizationsStore.setSelectedOrganization(organizationToSelect)
      navigationStore.setLastOrganizationURLPath(organizationToSelect.urlPath)
      lastOrganizationId.value = organizationToSelect.id

      // ✅ AJOUTER aussi la gestion d'erreur ici
      try {
        await workspacesStore.getWorkspaces()
      }
      catch (error) {
        console.error('Error fetching workspaces:', error)
        const { $sentryCaptureException } = useNuxtApp()
        $sentryCaptureException(error)
        // Continuer l'exécution même si les workspaces échouent
      }
    }
    else if (to.params.organizationURLPath) {
      return navigateTo(AppRoutes.SelectOrganization)
    }
  }

  if (selectedOrganization.value) {
    const suspendedRoutePath = getOrganizationRoutePath(OrganizationRoutes.Suspended)
    const baseRoutePath = getOrganizationRoutePath(OrganizationRoutes.Base)
    const isGranted = isCurrentUserGrantedInOrganization(OrganizationRoles.Viewer)

    if (isGranted && to.path === suspendedRoutePath)
      return navigateTo(baseRoutePath)

    if (!isGranted && to.path !== suspendedRoutePath)
      return navigateTo(suspendedRoutePath)

    // Check specific organization access
    if (to.meta.organizationAccesses && to.meta.organizationAccesses.minimumRole) {
      const { minimumRole } = to.meta.organizationAccesses
      if (!isCurrentUserGrantedInOrganization(minimumRole)) return navigateTo(baseRoutePath)
    }

    if (!to.params.workspaceURLPath) {
      navigationStore.setLastWorkspaceURLPath('')
      workspacesStore.setSelectedWorkspace(null)
      return
    }

    if (to.params.workspaceURLPath && lastWorkspaceURLPath.value !== to.params.workspaceURLPath) {
      const workspaceToSelect = workspaces.value.find(
        workspace => workspace.workspace.urlPath === to.params.workspaceURLPath,
      )

      if (workspaceToSelect) {
        workspacesStore.setSelectedWorkspace(workspaceToSelect)
        navigationStore.setLastWorkspaceURLPath(to.params.workspaceURLPath as string)
      }
      else {
        return navigateTo(getOrganizationRoutePath(OrganizationRoutes.Base))
      }
    }
  }
})
